import { FixValidator } from "./FixValidator";
import { FixTag } from "./model/FixTag";
import { FixMessage } from "./model/FixMessage";

const Assert = {
  assertNotNull: function (value: any, s: string) {
    if (!value) {
      throw new Error(s);
    }
  },
  assertEquals: function (value: any, expected: any, s: string) {
    if (value !== expected) {
      throw new Error("Expected " + expected + " but was " + value + ", " + s);
    }
  },
};

export class FixParser {
  readonly jsPrefix: string = "js";

  private readonly TAGVALUE_REGEX = /^\s?([0-9]+)\s?=\s?(.*)\s?$/;
  private dialect = "auto";
  private readonly validator: FixValidator = new FixValidator(this);
  private readonly onMessage: (
    fixMessage: FixMessage,
    rawMessage: string,
    curNum: number,
    totalNum: number
  ) => void = () => {
    // do nothing
  };
  private readonly onComplete: (cnt: number) => void;
  private parsedMessages = 0;
  private totalMessages = 0;

  constructor(
    jsPath: string,
    onMessage: (
      fixMessage: FixMessage,
      rawMessage: string,
      curNum: number,
      totalNum: number
    ) => void,
    onComplete: (cnt: number) => void
  ) {
    if (jsPath !== undefined) {
      this.jsPrefix = jsPath;
    } else {
      this.jsPrefix = "js";
    }
    this.dialect = "auto";

    /**
     * @public
     * @param fixMessage
     * @param rawMessage
     * @param curNum
     * @param totalNum
     */
    this.onMessage = onMessage;

    /**
     * @public
     * @param cnt
     */
    this.onComplete = onComplete;
  }

  private normalizeMassage(message: string): string {
    return message
      .replace(/<SOH>/g, "|")
      .replace(/\u0001/g, "|")
      .replace(/\^A/g, "|")
      .replace(/☺/g, "|")
      .replace(/;/g, "|")
      .replace(/(\\u0001)/g, "|");
  }

  async parseMessage(message: string): Promise<FixMessage> {
    //console.log("Begin parsing rawMessage:", message);
    const tagRegex = this.TAGVALUE_REGEX;
    const rawMessage = this.normalizeMassage(message);
    const rawTags = rawMessage.split("|"),
      msg = new FixMessage(rawMessage),
      tags = [];
    for (let i = 0; i < rawTags.length; i++) {
      if (rawTags[i].length === 0) {
        continue;
      }
      const rawTag = rawTags[i],
        m = tagRegex.exec(rawTag);
      if (m === null || m.length !== 3) {
        tags[tags.length] = rawTag;
        msg.valid = false;
        continue;
      }
      tags[tags.length] = new FixTag(msg, parseInt(m[1]), m[2].trim());
    }

    this.parseMessageHeader(msg, tags);

    for (let i = 0; i < tags.length; i++) {
      msg.addTag(tags[i]);
    }

    await this.validateMessage(msg);

    return Promise.resolve(msg);
  }

  private parseMessageHeader(msg: FixMessage, tags: Array<FixTag | string>) {
    //        console.log("Message:", msg, tags);
    const beginString = tags[0];
    Assert.assertNotNull(beginString, "VersionTag expected");
    if (beginString.num !== 8) {
      msg.valid = false;
      return;
    }

    Assert.assertEquals(beginString.num, 8, "UnexpectedTag");
    Assert.assertNotNull(beginString.value, "Version value expected");

    msg.beginString = beginString.value;

    for (let i = 0; i < tags.length; i++) {
      switch (tags[i].num) {
        case 8:
          msg.beginString = tags[i].value;
          break;
        case 9:
          msg.bodyLength = parseInt(tags[i].value);
          break;
        case 35:
          msg.msgType = tags[i];
          break;
        case 49:
          msg.senderCompId = tags[i].value;
          break;
        case 50:
          msg.senderSubId = tags[i].value;
          break;
        case 56:
          msg.targetCompId = tags[i].value;
          break;
        case 57:
          msg.targetSubId = tags[i].value;
          break;
      }
    }
  }

  async validateMessage(msg: FixMessage) {
    if (!msg.msgType) {
      msg.valid = false;
      msg.errors.push("No Message Type specified.");
      return;
    }
    if (this.dialect === "auto" || this.dialect === undefined) {
      msg.dialect = msg.beginString;
    } else {
      msg.dialect = this.dialect;
    }

    return this.validator.validateMessage(msg, msg.dialect).then(
      () => {
        // do nothing
      },
      () => {
        // do nothing
      }
    );
  }

  /**
   * @public
   * @param {string} rawText
   * @param {string} dialect
   */
  public async parse(rawText: string, dialect: string) {
    this.parsedMessages = 0;
    this.totalMessages = 0;
    if (!!dialect) {
      this.dialect = dialect;
    }

    const lines = rawText.split("\n");
    const msgs = [];
    for (let j = 0; j < lines.length; j++) {
      const start = lines[j].indexOf("8=FIX");
      if (start !== -1) {
        const rawMessage = lines[j].substring(start).trim();
        if (rawMessage.length === 0) {
          continue;
        }
        msgs[msgs.length] = rawMessage;
      }
    }
    this.totalMessages = msgs.length;
    if (this.totalMessages > 0) {
      return Promise.all(msgs.map((it) => this.parseMessage(it)));
    } else {
      this.onComplete(0);
      return Promise.resolve();
    }
  }

  onMessageParsed(msg: FixMessage) {
    this.parsedMessages++;
    //console.log("Parsed " + this.parsedMessages + " message of " + this.totalMessages);
    this.onMessage(
      msg,
      msg.normalizedSource(),
      this.parsedMessages,
      this.totalMessages
    );
    if (this.parsedMessages === this.totalMessages) {
      this.onComplete(this.totalMessages);
    }
  }
}
